import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, Loader2, AlertTriangle } from 'lucide-react';
import { isTerminalProvisioned, setTerminalToken, TERMINAL_CLEARED_EVENT } from '../../services/TerminalService';
import { validateTerminalToken } from '../../services/AuthService';
import './TerminalProvision.css';

/**
 * Alta del TPV en el primer arranque: pide el codigo del terminal, que se da en
 * codigo de barras. El lector actua de teclado: teclea el codigo y pulsa Enter.
 *
 * Solo se guarda si el servidor lo reconoce, y una vez guardado no se vuelve a
 * pedir (queda en localStorage de esta maquina).
 */
function ProvisionScreen({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // El lector escribe donde este el foco: siempre en el campo.
  useEffect(() => { inputRef.current?.focus(); }, [isChecking, error]);

  const submit = async () => {
    // Algunos lectores añaden saltos o espacios al final.
    const token = code.replace(/\s+/g, '');
    if (!token || isChecking) return;
    setIsChecking(true);
    setError(null);
    try {
      await validateTerminalToken(token);
      setTerminalToken(token);
      onDone();
    } catch (err) {
      setCode('');
      setError((err as any)?.message || 'No se pudo validar el código.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="tp-prov-root">
      <div className="tp-prov-card">
        <div className="tp-prov-icon"><ScanLine size={36} /></div>
        <h1 className="tp-prov-title">Activar este TPV</h1>
        <p className="tp-prov-text">Escanea el código de barras del terminal. Solo se pide esta vez.</p>

        <input
          ref={inputRef}
          type="password"
          autoComplete="off"
          className="tp-prov-input"
          placeholder="Esperando lectura…"
          value={code}
          disabled={isChecking}
          onChange={(e) => setCode(e.target.value)}
          onBlur={() => { if (!isChecking) inputRef.current?.focus(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          data-testid="terminal-code"
        />

        {isChecking && (
          <p className="tp-prov-status"><Loader2 size={18} className="tp-prov-spin" /> Validando…</p>
        )}
        {error && (
          <p className="tp-prov-error" role="alert"><AlertTriangle size={18} /> {error}</p>
        )}

        <button className="tp-prov-btn" disabled={!code.trim() || isChecking} onClick={submit}>
          Validar
        </button>
      </div>
    </div>
  );
}

/** Muestra la pantalla de alta hasta que el terminal tenga su codigo guardado. */
export function TerminalGate({ children }: { children: React.ReactNode }) {
  const [provisioned, setProvisioned] = useState(isTerminalProvisioned());

  // Si el codigo guardado falla al conectar, se borra y hay que pedirlo otra vez.
  useEffect(() => {
    const onCleared = () => setProvisioned(false);
    window.addEventListener(TERMINAL_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(TERMINAL_CLEARED_EVENT, onCleared);
  }, []);

  if (!provisioned) return <ProvisionScreen onDone={() => setProvisioned(true)} />;
  return <>{children}</>;
}
